import type {
  CheckStatus,
  PcfValidationReport,
  SeverityLevel,
  ValidationCheckResult,
  ValidationIssue,
} from '../types';
import { normalizeValidationId } from '../validationConstants';

const ACCEPTED_END_TYPE_PAIR_KEYS = new Set(['BV|SW']);
const EMPTY_END_TYPE_DISPLAY_TOKENS = new Set(['<EMPTY>', '<MISSING>', '(EMPTY)', '(MISSING)']);

function normalizeEndTypeToken(value: string | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed || EMPTY_END_TYPE_DISPLAY_TOKENS.has(trimmed.toUpperCase())) {
    return '';
  }

  return trimmed.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function buildEndTypePairKey(left: string, right: string): string {
  return left <= right ? `${left}|${right}` : `${right}|${left}`;
}

function getEndTypeCandidates(issue: ValidationIssue, side: 'current' | 'connected'): string[] {
  const prefix = side === 'current' ? 'current' : 'connected';
  const additionalData = issue.AdditionalData ?? {};

  return [
    additionalData[`${prefix}EndTypeRaw`],
    additionalData[`${prefix}EndTypeCanonical`],
    additionalData[`${prefix}EndTypeCompatibility`],
  ]
    .map(normalizeEndTypeToken)
    .filter(Boolean);
}

function extractQuotedMessageEndTypes(message: string): string[] {
  const values: string[] = [];
  const source = message || '';
  const quotedValuePattern = /['"]([^'"]+)['"]/g;
  let match = quotedValuePattern.exec(source);

  while (match) {
    const normalized = normalizeEndTypeToken(match[1]);
    if (normalized) {
      values.push(normalized);
    }
    match = quotedValuePattern.exec(source);
  }

  return values;
}

function isAcceptedEndTypePair(left: string, right: string): boolean {
  if (!left || !right) {
    return false;
  }

  return ACCEPTED_END_TYPE_PAIR_KEYS.has(buildEndTypePairKey(left, right));
}

export function isAcceptedEndTypeConnectivityIssue(issue: ValidationIssue): boolean {
  if (normalizeValidationId(issue.ValidationId) !== 11) {
    return false;
  }

  const currentCandidates = getEndTypeCandidates(issue, 'current');
  const connectedCandidates = getEndTypeCandidates(issue, 'connected');
  for (const current of currentCandidates) {
    if (connectedCandidates.some((connected) => isAcceptedEndTypePair(current, connected))) {
      return true;
    }
  }

  const quotedValues = extractQuotedMessageEndTypes(issue.Message);
  if (quotedValues.length >= 2 && isAcceptedEndTypePair(quotedValues[0], quotedValues[1])) {
    return true;
  }

  return false;
}

export function getActionableValidationIssues(check: ValidationCheckResult): ValidationIssue[] {
  return (check.Issues ?? []).filter((issue) => !isAcceptedEndTypeConnectivityIssue(issue));
}

function resolveStatusFromIssues(issues: ValidationIssue[]): CheckStatus {
  if (issues.length === 0) {
    return 'Passed';
  }

  return issues.some((issue) => issue.Severity === 'Critical' || issue.Severity === 'Error')
    ? 'Failed'
    : 'Warning';
}

function countIssuesBySeverity(issues: ValidationIssue[], severity: SeverityLevel): number {
  return issues.filter((issue) => issue.Severity === severity).length;
}

function recomputeReportTotals(report: PcfValidationReport): void {
  const allIssues = (report.CheckResults ?? []).flatMap((check) => check.Issues ?? []);
  report.TotalIssues = allIssues.length;
  report.CriticalIssues = countIssuesBySeverity(allIssues, 'Critical');
  report.ErrorIssues = countIssuesBySeverity(allIssues, 'Error');
  report.WarningIssues = countIssuesBySeverity(allIssues, 'Warning');
  report.InfoIssues = countIssuesBySeverity(allIssues, 'Info');
  report.FailedChecks = (report.CheckResults ?? []).filter((check) => check.Status === 'Failed').length;
  report.WarningChecks = (report.CheckResults ?? []).filter((check) => check.Status === 'Warning').length;
  report.PassedChecks = (report.CheckResults ?? []).filter((check) => check.Status === 'Passed').length;
  report.IsExportReady = report.FailedChecks === 0 && report.CriticalIssues === 0 && report.ErrorIssues === 0;
  report.IssuesByCategory = allIssues.reduce<Record<string, number>>((counts, issue) => {
    const category = issue.Category || 'Uncategorized';
    counts[category] = (counts[category] ?? 0) + 1;
    return counts;
  }, {});
}

export function sanitizeAcceptedEndTypeConnectivityIssues(report: PcfValidationReport): PcfValidationReport {
  let removedAnyIssue = false;

  for (const check of report.CheckResults ?? []) {
    const issues = check.Issues ?? [];
    const actionableIssues = getActionableValidationIssues(check);
    if (actionableIssues.length === issues.length) {
      continue;
    }

    check.Issues = actionableIssues;
    check.IssueCount = actionableIssues.length;
    check.HasIssues = actionableIssues.length > 0;
    check.Status = resolveStatusFromIssues(actionableIssues);
    removedAnyIssue = true;
  }

  if (removedAnyIssue) {
    recomputeReportTotals(report);
  }

  return report;
}
